import type {
  InstrumentApiResponse,
  ReferenceDataField,
} from './instrument-data'
import type { BondType } from './reference-data'

import { Operator } from './filter'

export type FilterOperator = Operator

type FilterReturnValue<R extends ReferenceDataField> = {
  [Operator.EqualTo]: InstrumentApiResponse[R] | null
  [Operator.NotEqualTo]: InstrumentApiResponse[R] | null
  [Operator.GreaterThan]: InstrumentApiResponse[R] | null
  [Operator.GreaterThanOrEqualTo]: InstrumentApiResponse[R] | null
  [Operator.LessThan]: InstrumentApiResponse[R] | null
  [Operator.LessThanOrEqualTo]: InstrumentApiResponse[R] | null
  [Operator.In]: InstrumentApiResponse[R][] | null
  [Operator.NotIn]: InstrumentApiResponse[R][] | null
  [Operator.IsNull]: boolean
  [Operator.Includes]: InstrumentApiResponse[R] | null
  [Operator.NotIncludes]: InstrumentApiResponse[R] | null
  [Operator.StartsWith]: string | null
  [Operator.NotStartsWith]: string | null
  [Operator.EndsWith]: string | null
  [Operator.InFilterList]: string | null
  [Operator.NotInFilterList]: string | null
  [Operator.NotInList]: InstrumentApiResponse[R][] | null
}

// This type signifies that every key in the overall object must be a ref data field, rather than an arbitrary string,
// and the return value is a function of that specific ref data field and the operator. For example, the value for
// equal_to should be a string for field isin and a number for field duration.

export type FilterCondition = Partial<
  {
    [R in ReferenceDataField]: {
      [operator in FilterOperator]?: FilterReturnValue<R>[operator]
    }
  } & {
    // yield_to_worst is a field on *MarketSideMetadata* (Bids+Asks)
    // but it is the preferred filter field (instead of 'close_ytw')
    //
    // Adding it here b/c this allows us to keep the ReferenceDataField type clean
    //
    // Currently it is the only non-ref data field we filter Live Inventory on
    yield_to_worst: {
      [operator in FilterOperator]?: number | null
    }
    yield_to_maturity: {
      [operator in FilterOperator]?: number | null
    }
    price: {
      [operator in FilterOperator]?: number | null
    }
    // HACK: These fields are duplicated from BuyUniverseField
    // TODO: Consolidate these fields into a more maintainable type structure
    // that properly shares types with BuyUniverseField instead of duplicating them here
    fixed_transaction_cost_in_price: {
      [operator in FilterOperator]?: number | null
    }
    fixed_transaction_cost_in_yield: {
      [operator in FilterOperator]?: number | null
    }
    maturity_in_years: {
      [operator in FilterOperator]?: number | null
    }
    next_call_in_years: {
      [operator in FilterOperator]?: number | null
    }
    // Absolute maturity-date fields (ISO 'yyyy-MM-dd' strings). Used by the
    // ladder-policy card to bracket rungs by date, matching suboptimal's
    // date-based bucketing rather than the *_in_years approximations.
    maturity_date: {
      [operator in FilterOperator]?: string | null
    }
    next_call_date: {
      [operator in FilterOperator]?: string | null
    }
    workout_date: {
      [operator in FilterOperator]?: string | null
    }
    order_price: {
      [operator in FilterOperator]?: number | null
    }
    order_ytw: {
      [operator in FilterOperator]?: number | null
    }
    tax_effective_limit_ytw: {
      [operator in FilterOperator]?: number | null
    }
    workout_in_years: {
      [operator in FilterOperator]?: number | null
    }
    maturity_in_days: {
      [operator in FilterOperator]?: number | null
    }
    next_call_in_days: {
      [operator in FilterOperator]?: number | null
    }
    sp_creditwatch_in_days: {
      [operator in FilterOperator]?: number | null
    }
    ytw: {
      [operator in FilterOperator]?: number | null
    }
    below_de_minimis_threshold: {
      [operator in FilterOperator]?: string | null
    }
    tax_state: {
      [operator in FilterOperator]?: string | null
    }
    management_type: {
      [operator in FilterOperator]?: string | null
    }
    custodian: {
      [operator in FilterOperator]?: string | null
    }
    de_minimis_threshold_distance: {
      [operator in FilterOperator]?: number | null
    }
    asset_type: {
      [operator in Exclude<FilterOperator, 'in' | 'not_in'>]?: string | null
    } & {
      in?: string[] | null
      not_in?: string[] | null
    }
  }
>

export enum CompoundExpressionOperator {
  And = 'and',
  Or = 'or',
}

export type OrCondition = {
  or: FilterCondition[]
}

export type AndCondition = {
  and: FilterCondition[]
}

export type NotCondition = {
  not: FilterObject
}

export type FilterObject =
  | FilterCondition
  | OrCondition
  | AndCondition
  | NotCondition

/**
 * Type guards for Filter Object
 */
export function isOrCondition(filter: FilterObject): filter is OrCondition {
  return 'or' in filter && Array.isArray((filter as OrCondition).or)
}

export function isAndCondition(filter: FilterObject): filter is AndCondition {
  return 'and' in filter && Array.isArray((filter as AndCondition).and)
}

export function isNotCondition(filter: FilterObject): filter is NotCondition {
  return 'not' in filter && typeof (filter as NotCondition).not === 'object'
}

/**
 * Intersection view of FilterObject: one object can carry plain field keys
 * and compound keys at once (the API ANDs all top-level keys).
 */
type MixedFilterObject = FilterCondition &
  Partial<OrCondition & AndCondition & NotCondition>

/**
 * Merges filter objects without letting compound keys (or/and/not) collide.
 * Plain field keys are combined onto one object (the API ANDs top-level keys);
 * multiple compound groups are nested under a single `and`.
 */
export function mergeFilterObjects(...filters: FilterObject[]): FilterObject {
  const fields: FilterCondition = {}
  const groups: FilterObject[] = []

  for (const filter of filters) {
    const { or, and, not, ...rest } = filter as MixedFilterObject
    if (and) {
      groups.push(...and)
    }
    if (or) {
      groups.push({ or })
    }
    if (not) {
      groups.push({ not })
    }
    Object.assign(fields, rest)
  }

  if (groups.length === 0) {
    return fields
  }
  if (groups.length === 1) {
    return { ...fields, ...groups[0] }
  }
  // The API accepts nested compound groups under `and`; AndCondition's
  // FilterCondition[] is narrower than the backend grammar.
  return { ...fields, and: groups as FilterCondition[] }
}

export function generateFilterObject(
  type: BondType | null,
  searchTerm?: string | null,
  quickFilter: FilterObject | null = null,
  filtersFromPanel: FilterObject | null = null,
): FilterObject {
  const baseFilter: FilterObject = {}

  if (type) {
    baseFilter.type = { equal_to: type }
  }

  const filterObject = mergeFilterObjects(
    baseFilter,
    quickFilter ?? {},
    filtersFromPanel ?? {},
  )

  if (searchTerm) {
    return mergeFilterObjects(filterObject, {
      or: [
        { description: { includes: searchTerm } },
        { description_short: { includes: searchTerm } },
        { isin: { includes: searchTerm } },
        { ticker: { starts_with: searchTerm } },
      ],
    })
  }

  return filterObject
}
